/* eslint-disable react/no-danger */
import { useRef, useState, useCallback, useMemo, MouseEvent } from 'react';

import { Typography, Icon, Button, Box, Popover } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { LightTooltip } from 'components';
import {
  DefineColumn,
  CompareFunction,
  Order,
  SearchParams,
} from 'components/DataTable/types';
import { PaginationReturn } from 'generated';
import { useDataTestId } from 'hooks';

import { FloatSearch } from '..';
import { useStyles } from './styles';

interface CellRenderProps<T = any> {
  defineColumn: DefineColumn;
  onSearch(value: string, searchParam: keyof T): void;
  onSort(property: keyof T, compareFunction?: CompareFunction): void;
  order: Order;
  orderBy: keyof T | null;
  hasSeparator?: boolean;
  maxWidth?: string | number;
  minWidth?: string | number;
  loading?: boolean;
  searchParams: SearchParams<T>;
  pagination?: PaginationReturn;
}

export function CellRender<T = any>({
  defineColumn,
  onSearch,
  onSort,
  order,
  orderBy,
  minWidth,
  maxWidth,
  loading,
  searchParams,
  pagination,
  hasSeparator = true,
}: CellRenderProps<T>): JSX.Element {
  const classes = useStyles();
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const { getTestId } = useDataTestId();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const memoSearchMenuIsOpen = useMemo(() => !!anchorEl, [anchorEl]);

  const memoHasSearchParams = useMemo(() => {
    return !!Object.values(searchParams).length;
  }, [searchParams]);

  const handleToggleSearch = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(oldState => {
        if (!oldState) {
          return event.currentTarget;
        }

        return null;
      });
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      if (loading) {
        return;
      }

      onSearch(value, defineColumn.id as keyof T);
      searchButtonRef.current?.blur();
    },
    [defineColumn.id, loading, onSearch],
  );

  const handleSort = useCallback(() => {
    if (loading) {
      return;
    }

    onSort(defineColumn.id as keyof T, defineColumn.compareFunction);
  }, [defineColumn.compareFunction, defineColumn.id, loading, onSort]);

  return (
    <div
      {...getTestId(`col-${defineColumn.id}`)}
      className={clsx(classes.root, {
        [classes.separator]: hasSeparator,
      })}
      style={{ maxWidth, minWidth, paddingLeft: loading ? 4 : undefined }}
    >
      {defineColumn.hasTooltip ? (
        <LightTooltip
          ref={tooltipRef}
          title={defineColumn.label}
          placement="top-start"
        >
          <Typography
            variant="subtitle2"
            component="div"
            noWrap
            className={clsx(classes.label, {
              [classes.bold]: defineColumn.labelIsBold,
              [classes.sorted]: orderBy === defineColumn.id,
            })}
          >
            {loading ? (
              <Skeleton
                animation="wave"
                className={classes.skeleton}
                width="100%"
              />
            ) : (
              defineColumn.label
            )}
          </Typography>
        </LightTooltip>
      ) : (
        <Typography
          variant="subtitle2"
          component="div"
          noWrap
          className={clsx(classes.label, {
            [classes.bold]: defineColumn.labelIsBold,
            [classes.sorted]: orderBy === defineColumn.id,
          })}
        >
          {loading ? (
            <Skeleton
              animation="wave"
              className={classes.skeleton}
              width="100%"
            />
          ) : (
            defineColumn.label
          )}
        </Typography>
      )}
      {(defineColumn.searchable || defineColumn.sortable) &&
        (!pagination || pagination.totalCount > 1 || memoHasSearchParams) && (
          <Box
            display="flex"
            alignItems="center"
            className={classes.actionsContainer}
          >
            {defineColumn.searchable &&
              (!pagination ||
                pagination.totalCount > 1 ||
                memoHasSearchParams) && (
                <>
                  <LightTooltip
                    placement="top"
                    title={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: `Pesquisar por <b>${defineColumn.label}</b>`,
                        }}
                      />
                    }
                  >
                    <Button
                      {...getTestId(`search-button-${defineColumn.id}`)}
                      aria-haspopup="true"
                      aria-label="Search"
                      size="small"
                      color={memoHasSearchParams ? 'primary' : undefined}
                      aria-controls={
                        memoSearchMenuIsOpen ? 'menu-search-grow' : undefined
                      }
                      className={classes.actionButton}
                      onClick={handleToggleSearch}
                      ref={searchButtonRef}
                    >
                      {loading ? (
                        <Skeleton
                          variant="circle"
                          animation="wave"
                          width={24}
                          height={24}
                        />
                      ) : (
                        <Icon
                          color={memoHasSearchParams ? 'primary' : 'inherit'}
                        >
                          search
                        </Icon>
                      )}
                    </Button>
                  </LightTooltip>
                  <Popover
                    id={memoSearchMenuIsOpen ? 'search-popover' : undefined}
                    open={memoSearchMenuIsOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <FloatSearch
                      searchParams={searchParams}
                      columnId={defineColumn.id}
                      label={defineColumn.searchLabel}
                      onClose={handleClose}
                      onClean={() => handleSearch('')}
                      onSearch={handleSearch}
                    />
                  </Popover>
                </>
              )}
            {defineColumn.sortable &&
              (!pagination ||
                pagination.totalCount > 1 ||
                memoHasSearchParams) && (
                <LightTooltip
                  placement="top"
                  title={
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          orderBy === defineColumn.id
                            ? `Ordenar <b>${defineColumn.label}</b> de forma ${
                                order === 'asc' ? 'decrescente' : 'crescente'
                              }`
                            : `Ordenar <b>${defineColumn.label}</b>`,
                      }}
                    />
                  }
                >
                  <Button
                    {...getTestId(`sort-button-${defineColumn.id}`)}
                    aria-label="Sort direction"
                    size="small"
                    onClick={handleSort}
                    color={orderBy === defineColumn.id ? 'primary' : undefined}
                    className={clsx(classes.actionButton, {
                      [classes.sorted]: orderBy === defineColumn.id,
                    })}
                  >
                    {loading ? (
                      <Skeleton
                        variant="circle"
                        animation="wave"
                        width={24}
                        height={24}
                      />
                    ) : (
                      <Icon
                        className={clsx(classes.sort, {
                          desc: order === 'desc' && orderBy === defineColumn.id,
                        })}
                      >
                        arrow_downward
                      </Icon>
                    )}
                  </Button>
                </LightTooltip>
              )}
          </Box>
        )}
    </div>
  );
}
